import {skipToken} from '@reduxjs/toolkit/query';
import {useLocale} from 'platform/locale';

import {useMemo} from 'react';

import {isNil, isNotNil} from 'ramda';

import {useDrillDownQuery} from '../../../../../store/omneticApi';
import {DrillDownApiArg, DrillDownResponse} from '../../../../../types/api/drillDown';
import {filterStatisticsAggregatedByMinYear} from '../utils/filterStatisticsAggregatedByMinYear';
import {mergeGlobalRowsWithMarketStatistics} from '../utils/mergeGlobalRowsWithMarketStatistics';

type Arg = DrillDownApiArg & {minYear?: number};

/**
 * Fetches drill-down with global data for row structure and region-specific data for counts,
 * then merges: rows from global query, statistics (counts) from regions query.
 * Same data strategy as useGetCounts in useFilterRelevantOptions.
 * When minYear is set, only rows with year level >= minYear are included.
 * Pass skipToken as first argument to skip both queries (same as RTK Query).
 */
export function useDrillDownWithGlobalRowsMarketCounts(
  arg: Arg | typeof skipToken,
  options?: {skip?: boolean}
) {
  const {language} = useLocale();
  const isSkipped = arg === skipToken;
  const skip = isSkipped || (options?.skip ?? false);

  const globalQueryArg: DrillDownApiArg | typeof skipToken = isSkipped
    ? skipToken
    : {
        levels: arg.levels,
        currency: arg.currency,
        lang: arg.lang ?? language,
        regions: [],
      };
  const regionsQueryArg: DrillDownApiArg | typeof skipToken = isSkipped
    ? skipToken
    : {
        levels: arg.levels,
        currency: arg.currency,
        lang: arg.lang ?? language,
        regions: arg.regions,
      };

  const {
    data: globalData,
    isLoading: isLoadingGlobal,
    isFetching: isFetchingGlobal,
    isError: isErrorGlobal,
  } = useDrillDownQuery(globalQueryArg, {skip});

  const {
    data: marketData,
    isLoading: isLoadingMarket,
    isFetching: isFetchingMarket,
    isError: isErrorMarket,
  } = useDrillDownQuery(regionsQueryArg, {skip});

  const data = useMemo((): DrillDownResponse | undefined => {
    if (arg === skipToken || isNil(globalData)) {
      return undefined;
    }
    const merged = mergeGlobalRowsWithMarketStatistics(globalData, marketData);
    if (isNotNil(arg.minYear)) {
      return {
        ...merged,
        statisticsAggregated: filterStatisticsAggregatedByMinYear(
          merged.statisticsAggregated,
          arg.minYear
        ),
      };
    }
    return merged;
  }, [globalData, marketData, arg]);

  return {
    data,
    isLoading: isLoadingGlobal || isLoadingMarket,
    isFetching: isFetchingGlobal || isFetchingMarket,
    isError: isErrorGlobal || isErrorMarket,
  };
}
